// components/Header.jsx
'use client'

import { useAuth } from '@/contexts/AuthContext'
import { useMobileMenu } from '@/contexts/MobileMenuContext'
import { useTheme } from '@/contexts/ThemeContext'
import { useRouter } from 'next/navigation'
import { FaExchangeAlt, FaSignOutAlt, FaBars, FaTimes, FaSun, FaMoon, FaDesktop } from 'react-icons/fa'
import { useState, useRef, useEffect } from 'react'
import { APP_VERSION_LABEL } from '@/utils/appVersion'
import { isAccountType, isOrgAccountType } from '@/utils/builtInApplets'

// ─── Theme Toggle Button ───────────────────────────────────────────────────────
function ThemeToggle() {
    const { theme, resolvedTheme, setTheme } = useTheme()
    const [open, setOpen] = useState(false)
    const menuRef = useRef(null)

    // Close on outside click
    useEffect(() => {
        if (!open) return
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const options = [
        { value: 'light',  label: 'Light',  Icon: FaSun },
        { value: 'dark',   label: 'Dark',   Icon: FaMoon },
        { value: 'system', label: 'System', Icon: FaDesktop },
    ]

    const ActiveIcon = resolvedTheme === 'dark' ? FaMoon : FaSun

    return (
        <div className="relative" ref={menuRef}>
            {/* Toggle button */}
            <button
                onClick={() => setOpen(prev => !prev)}
                className="
                    flex items-center justify-center
                    w-9 h-9 sm:w-10 sm:h-10
                    rounded-lg text-gray-300
                    hover:text-white hover:bg-gray-800
                    active:bg-gray-700 transition-colors touch-target
                "
                title="Change theme"
                aria-label="Change theme"
                aria-expanded={open}
                aria-haspopup="menu"
            >
                <ActiveIcon className="text-sm sm:text-base" />
            </button>

            {/* Dropdown */}
            {open && (
                <div
                    role="menu"
                    className="
                        absolute right-0 top-full mt-2 w-36
                        rounded-lg shadow-xl overflow-hidden
                        border border-[var(--card-border)]
                        bg-[var(--card-bg)]
                        animate-scale-in z-50
                    "
                >
                    {options.map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            role="menuitem"
                            onClick={() => { setTheme(value); setOpen(false) }}
                            className={`
                                w-full flex items-center gap-2.5 px-3 py-2.5
                                text-sm transition-colors text-left
                                ${theme === value
                                ? 'bg-amber-500/10 text-amber-500 font-medium'
                                : 'text-[var(--foreground)] hover:bg-[var(--secondary-hover)]/10'
                            }
                            `}
                        >
                            <Icon className="text-xs flex-shrink-0" />
                            {label}
                            {theme === value && (
                                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

// ─── Main Header ───────────────────────────────────────────────────────────────
export default function Header({ showMenuButton = true }) {
    const { logout, isAuthenticated, currentOrg, organizations } = useAuth()
    const { isMobileOpen, toggleMobileMenu } = useMobileMenu()
    const router = useRouter()

    const handleLogout = () => {
        logout()
        router.push('/login')
    }

    const handleSwitchOrg = () => {
        router.push('/memberships')
    }

    // Hide "Switch Organization" when there is nothing to switch to.
    //
    // Deliberately narrow. /memberships is not only the org switcher — it is
    // also where you JOIN an org and where "Create Organization" lives — so
    // hiding this for every single-org user would remove the only route to
    // both. A house-scoped account is different: a shared station login or a
    // HouseOnly org belongs to exactly one house and is not going to join or
    // create anything, so the control is pure noise in a header that is already
    // tight on a phone. Owner request 2026-08-08.
    //
    // The `<= 1` still matters: if such an account ever does belong to more than
    // one organization, switching is meaningful again and the button returns.
    //
    // A shared training-cal login is the same shape: legacy gives it
    // TrainingCalLayout (Shared/TrainingCalLayout.razor) — the FSE logo and a
    // Logout button, nothing else — so it gets the same single-org treatment
    // here. Audit 12h, OUTSTANDING §C57.
    const houseScoped =
        isOrgAccountType(currentOrg, 'HouseOnly') || isAccountType(currentOrg, 'SharedHouse')
    const sharedTrainingCalScoped = isAccountType(currentOrg, 'SharedTrainingCal')
    const canSwitchOrg = !((houseScoped || sharedTrainingCalScoped) && (organizations?.length ?? 0) <= 1)

    // Below md the top row has no room for the org name, so it gets a row of
    // its own. Without it a phone gave no sign of which org you were in: an
    // admin of two orgs deleted LAC's FORMS applet meaning to delete
    // Ventura's (2026-11-02). The class raises --header-height (globals.css)
    // so pages, sidebars and toasts clear the taller header.
    const orgName = isAuthenticated ? (currentOrg?.orgName || currentOrg?.name || '') : ''

    useEffect(() => {
        const root = document.documentElement
        root.classList.toggle('has-org-bar', !!orgName)
        return () => root.classList.remove('has-org-bar')
    }, [orgName])

    if (!isAuthenticated) {
        return null
    }

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-black safe-area-top">
            {/* Main Header Row */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-2 min-h-[56px]">
                {/* Left: Hamburger + Logo and Version */}
                <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                    {/* Mobile Hamburger Button */}
                    {showMenuButton && (
                        <button
                            onClick={toggleMobileMenu}
                            className="
                                lg:hidden flex items-center justify-center
                                w-10 h-10 sm:w-11 sm:h-11
                                bg-gray-800 text-white rounded-lg
                                hover:bg-gray-700 active:bg-gray-600
                                transition-colors touch-target flex-shrink-0
                            "
                            aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={isMobileOpen}
                        >
                            {isMobileOpen ? (
                                <FaTimes className="text-lg sm:text-xl" />
                            ) : (
                                <FaBars className="text-lg sm:text-xl" />
                            )}
                        </button>
                    )}

                    {/* Organization Logo.

                        min-w-0 + max-w-full, NOT flex-shrink-0: the left
                        cluster is allowed to shrink (min-w-0 on the parent),
                        and a shrink-proof logo inside a shrinking cluster does
                        not stay put — it overflows the cluster's box and slides
                        underneath the right-side icons. Reported 2026-08-14 on
                        a phone: the theme toggle sat on top of the logo's right
                        edge. Letting the image scale down keeps it inside its
                        lane on any width; object-contain preserves the aspect
                        ratio as it shrinks. */}
                    <div className="relative min-w-0 flex items-center h-10 sm:h-12">
                        <img
                            src="/fse_logo.png"
                            alt="FireSync Enterprise"
                            className="h-10 sm:h-12 w-auto max-w-full object-contain"
                        />
                    </div>

                    {/* Version — from utils/appVersion, never hardcoded here.
                        This literal used to read "Version 11.8" while the login
                        screen said "Version 1.0" and error reports were tagged
                        with a third value. Three copies, none of them agreeing. */}
                    <span className="text-gray-400 text-xs sm:text-sm hidden sm:inline flex-shrink-0">
                        {APP_VERSION_LABEL}
                    </span>

                    {/* Current Org Name */}
                    {currentOrg && (
                        <>
                            <div className="hidden md:block w-px h-5 sm:h-6 bg-gray-600 flex-shrink-0" />
                            <span className="hidden md:block text-gray-300 text-xs sm:text-sm truncate max-w-[150px] lg:max-w-[250px] xl:max-w-[350px]">
                                {currentOrg.orgName || currentOrg.name}
                            </span>
                        </>
                    )}
                </div>

                {/* Right: Theme Toggle + Actions */}
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                    {/* Theme Toggle */}
                    <ThemeToggle />

                    {/* Divider */}
                    {canSwitchOrg && (
                    <div className="w-px h-5 bg-gray-700 mx-0.5 hidden sm:block" />
                    )}

                    {/* Switch Organization */}
                    {canSwitchOrg && (
                    <button
                        onClick={handleSwitchOrg}
                        className="
                            flex items-center gap-1.5 sm:gap-2
                            px-2 sm:px-3 py-1.5 sm:py-2 rounded-md
                            text-gray-300 hover:text-white hover:bg-gray-800
                            active:bg-gray-700 transition-colors
                            text-xs sm:text-sm touch-target
                        "
                        title="Switch Organization"
                    >
                        <FaExchangeAlt className="text-sm" />
                        <span className="hidden lg:inline sm:hidden">Switch Organization</span>
                    </button>
                    )}

                    {/* Logout */}
                    <button
                        onClick={handleLogout}
                        className="
                            flex items-center gap-1.5 sm:gap-2
                            px-2 sm:px-3 py-1.5 sm:py-2 rounded-md
                            text-blue-400 hover:text-blue-300 hover:bg-gray-800
                            active:bg-gray-700 transition-colors
                            text-xs sm:text-sm font-medium touch-target
                        "
                        title="Logout"
                    >
                        <FaSignOutAlt className="text-sm" />
                        <span className="hidden sm:inline">Logout</span>
                    </button>
                </div>
            </div>

            {/* Current Org Name — phones and small tablets (the top row shows it from md up) */}
            {orgName && (
                <div className="md:hidden h-6 px-3 sm:px-4 flex items-center border-t border-gray-800">
                    <span className="text-gray-200 text-xs font-medium truncate" title={orgName}>
                        {orgName}
                    </span>
                </div>
            )}

            {/* Accent Bar */}
            <div className="h-1.5 sm:h-2 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 opacity-90" />
        </header>
    )
}